import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

/**
 * Searchable multi-select dropdown matching the parchment theme.
 *
 * Controlled — pass an array of strings as `value`, get array back from
 * `onChange`. Options may be strings or numbers; they're coerced to strings
 * internally for comparison but passed back as strings.
 *
 * Used by Inventory filters (caliber, status) so the user can pick any
 * combination. Includes a type-to-filter search box at the top of the
 * dropdown for long lists (the watchmaker's caliber list is ~300 entries).
 */
export default function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = "All",
  testid,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }
  }, [open]);

  const selectedSet = new Set(value.map(String));
  const filtered = options
    .map(String)
    .filter((o) => !query || o.toLowerCase().includes(query.toLowerCase()));

  const toggle = (opt) => {
    const s = String(opt);
    const next = selectedSet.has(s)
      ? value.filter((v) => String(v) !== s)
      : [...value, s];
    onChange(next);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const label =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? String(value[0])
        : `${value.length} selected`;

  return (
    <div ref={ref} className={`relative ${className}`} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full h-10 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-[#0A0604] hover:bg-[#F4E9C9] transition-colors"
      >
        <span className={`truncate ${value.length === 0 ? "text-[#3A2A10]" : ""}`}>{label}</span>
        <div className="flex items-center gap-1">
          {value.length > 0 && (
            <span
              onClick={clear}
              role="button"
              tabIndex={0}
              className="p-0.5 hover:text-rose-500"
              data-testid={`${testid}-clear`}
              aria-label="Clear"
            >
              <X size={12} />
            </span>
          )}
          <ChevronDown size={14} className="text-[#3A2A10]" />
        </div>
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full max-h-[340px] overflow-hidden bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm shadow-lg flex flex-col">
          <div className="relative border-b border-[#8B6A1F]/25">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#3A2A10]" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              className="w-full h-9 pl-8 pr-2 bg-transparent text-sm text-[#0A0604] placeholder:text-[#3A2A10] focus:outline-none"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-xs text-[#3A2A10] text-center">No matches</div>
            ) : (
              filtered.map((o) => {
                const checked = selectedSet.has(o);
                return (
                  <button
                    key={o}
                    type="button"
                    onClick={() => toggle(o)}
                    className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors
                      ${checked ? "bg-[#D4AF37]/20 text-[#0A0604]" : "hover:bg-[#2B2214]/[0.06] text-[#3A2A10]"}`}
                    data-testid={`${testid}-option-${o}`}
                  >
                    <span className={`w-4 h-4 rounded-sm border flex items-center justify-center
                      ${checked ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#8B6A1F]/45"}`}>
                      {checked && <Check size={10} className="text-[#0A0C10]" />}
                    </span>
                    <span className="truncate flex-1">{o}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
